/*
 * The dropdown menu (Radix `DropdownMenu`): roving focus, typeahead and Escape come from the
 * primitive. Items are data rather than children so that a module can contribute menu entries
 * through a slot without importing this file's internals. An item that carries `checked` is a
 * setting rather than an action: it renders as a checkbox item, so a screen reader hears its state
 * and a pointer sees the mark. Items that share a `radioGroup` are one choice out of several and are
 * drawn as a radio group, so the reader is told "1 of 3" instead of being offered three independent
 * switches that happen to exclude each other.
 *
 * A menu reads as one column of labels whatever its rows carry: once one entry has an icon, every
 * row reserves the icon column, and once one entry is a toggle or an option, every row reserves the
 * mark column — so a label never jumps sideways between two neighbours.
 *
 * The way back out is this file's, not the primitive's (B4-11). Radix keeps the panel mounted for as
 * long as an exit animation runs and hands the focus back to the trigger only when it finally
 * unmounts, which is a focus move a tenth of a second after the reader chose something — long enough
 * to land in whatever they started typing in the meantime (measured: "Zum Plan" arrived as "Zum").
 * So the primitive's hand-over is declined and this file does it on the frame after the close, by
 * which time the reader's own action has had its say: a choice that moved the focus itself keeps it,
 * and a choice that moved nothing gets the trigger back. That is what lets the menu wear the exit
 * fade every other floating thing wears.
 *
 * While the menu is open the rest of the page is `inert`: nothing outside the panel takes the focus
 * or is read out, and a press outside still closes the menu. The primitive's own modal mode would
 * instead mark the rest `aria-hidden` and leave its buttons and links focusable — a page whose focusable
 * parts are hidden from assistive technology (axe's `aria-hidden-focus`) — so the menu is not modal
 * and does this itself, on every child of the body but the one that holds the panel, and lifts it the
 * moment the menu closes, before the focus is handed back.
 */

import * as RadixMenu from "@radix-ui/react-dropdown-menu"
import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from "react"
import { Icon, type IconName } from "../core/shell/icons.tsx"
import { cx } from "../lib/cx.ts"
import "./ui.css"

export interface MenuItem {
	id: string
	/** Already translated. */
	label: string
	/**
	 * A quiet line under the label, already translated: what the entry does when its label cannot
	 * say it all ("Runs next; the other requests stay held"), or why a disabled entry is disabled. It
	 * is part of the entry's name, so a screen reader hears it where an eye reads it.
	 */
	detail?: string
	onSelect: () => void
	disabled?: boolean
	/** A destructive action: red ink, and usually the last group of the menu. */
	danger?: boolean
	/** Draws a rule above this entry, so a long menu reads as its groups. */
	separatorBefore?: boolean
	/** Present on a toggle: the entry shows its state and `onSelect` flips it. */
	checked?: boolean
	/**
	 * The choice this entry is one option of. Consecutive entries naming the same choice are drawn as
	 * one radio group and `selected` says which option holds. Picking an option runs its own
	 * `onSelect`; picking the one that already holds reports nothing, which is the primitive's rule
	 * and the right one — a setting that is already set has not been changed.
	 */
	radioGroup?: string
	/** On an option of a `radioGroup`: whether this is the one that currently holds. */
	selected?: boolean
	/**
	 * What the choice is called, for assistive technology. Three options whose copy speaks for itself
	 * need no heading on screen, but the group they form does need a name — "group" alone says nothing
	 * about what is being chosen. Read from the first option that carries it.
	 */
	groupLabel?: string
	/**
	 * Drawn before the label at 16 px and hidden from assistive technology: the label is the name
	 * of the entry, the icon only repeats it for the eye. It is the same glyph the surface's own
	 * bar uses for that action, which is what ties a menu row to the button beside it.
	 */
	icon?: IconName
	/**
	 * The keyboard shortcut that does the same thing, as the reader would type it (`⌘K`, `Esc`).
	 * Printed at the end edge of the row and carried as `aria-keyshortcuts`, so the label stays
	 * the entry's whole name.
	 */
	shortcut?: string
	/**
	 * The same chord in the grammar `aria-keyshortcuts` defines, where a modifier is `Alt`, `Control`,
	 * `Meta` or `Shift` and alternatives are separated by a space (`Control+Shift+7 Meta+Shift+7`).
	 * What a reader sees and what assistive technology parses are two spellings of one chord, and
	 * `Ctrl` is not a name the specification knows; absent, the printed form is carried as it stands,
	 * which is right for a single key (`Esc`).
	 */
	shortcutAria?: string
	/**
	 * Present on an entry that opens a menu of its own beside it (`Export ▸`): its heading, if it has
	 * one, and its entries. Such an entry runs nothing itself — its `onSelect` is not called — and
	 * the arrow keys, the pointer and Enter open the inner menu as the primitive's submenu does.
	 */
	submenu?: { heading?: string; items: MenuItem[] }
	/**
	 * One step in from the entries around it: an entry that belongs under the one above it (a
	 * document's sub-tab under its tab). The step is drawn only; what it means is `spoken`'s.
	 */
	indent?: boolean
	/**
	 * What a screen reader hears in place of `label`, where the row's place says more to the eye than
	 * its words do ("Raw data, in Notes" for an indented "Raw data"). Already translated.
	 */
	spoken?: string
}

interface MenuProps {
	/** The control that opens the menu; it keeps its own accessible name. */
	trigger: ReactNode
	/**
	 * Something said above the first entry that is not an entry — the account menu's identity block
	 * (who is signed in, in words). It takes no focus and is not in the arrow walk; the caller paints
	 * it, the menu only gives it the room.
	 */
	header?: ReactNode
	items: MenuItem[]
	/**
	 * Present when the surface around the menu decides whether it is open — a row that answers
	 * `Shift+F10` for its own `⋯`, which a browser reports as a key and not as a click. Absent, the
	 * trigger is the only door and the primitive keeps the state itself.
	 */
	open?: boolean
	onOpenChange?: (open: boolean) => void
	/**
	 * Where the focus goes when the menu closes. The default is the trigger, which is right wherever
	 * the trigger is still there to take it — and wrong where the trigger only exists while the menu
	 * is open or the pointer is over it: a `.focus()` on a hidden element drops the focus to `<body>`,
	 * and the reader's next Tab starts again at the top of the document. A surface whose trigger can
	 * vanish names the element the reader came from instead: it is called on the frame after the
	 * close, and `event.preventDefault()` is how it says it has taken the focus itself.
	 */
	onCloseAutoFocus?: (event: Event) => void
	/**
	 * Which edge of the trigger the panel lines up with: `end`, the default, for a ⋯ at a row's
	 * end; `start` for a control the menu belongs under, such as a table's row or column handle.
	 */
	align?: "start" | "end"
}

/** What every row of one menu reserves room for, decided once from the whole list. */
interface Columns {
	icons: boolean
	marks: boolean
}

/** A run of entries drawn together: one radio group, or one plain entry. */
interface Block {
	radioGroup?: string
	items: MenuItem[]
}

/**
 * The entries as the menu draws them. Only *consecutive* entries of the same choice form a group, so
 * the order of the list is the order on screen and a caller that interleaves two choices gets two
 * groups rather than a reordered menu.
 */
function blocksOf(items: MenuItem[]): Block[] {
	const blocks: Block[] = []
	for (const item of items) {
		const open = blocks.at(-1)
		if (item.radioGroup !== undefined && open?.radioGroup === item.radioGroup) {
			open.items.push(item)
			continue
		}
		blocks.push(
			item.radioGroup === undefined
				? { items: [item] }
				: { radioGroup: item.radioGroup, items: [item] },
		)
	}
	return blocks
}

/** The mark column: the check of a toggle or an option that holds, or the empty room for one. */
function Mark({ shown }: { shown: boolean }) {
	return (
		<span className="tb-menu-item__mark" aria-hidden="true">
			{shown ? (
				<RadixMenu.ItemIndicator>
					<Icon name="check" size={16} />
				</RadixMenu.ItemIndicator>
			) : null}
		</span>
	)
}

/** The row's content in its columns: mark, icon, label, shortcut. */
function Row({ item, columns }: { item: MenuItem; columns: Columns }) {
	const toggle = item.checked !== undefined || item.radioGroup !== undefined
	return (
		<>
			{columns.marks ? <Mark shown={toggle} /> : null}
			{columns.icons ? (
				<span className="tb-menu-item__icon" aria-hidden="true">
					{item.icon === undefined ? null : <Icon name={item.icon} size={16} />}
				</span>
			) : null}
			<span className="tb-menu-item__label">
				{item.label}
				{item.detail === undefined ? null : (
					<span className="tb-menu-item__detail">{item.detail}</span>
				)}
			</span>
			{item.shortcut === undefined ? null : (
				<span className="tb-menu-item__shortcut" aria-hidden="true">
					{item.shortcut}
				</span>
			)}
		</>
	)
}

function rowClass(item: MenuItem): string {
	return cx(
		"tb-menu-item",
		item.danger === true && "tb-menu-item--danger",
		item.indent === true && "tb-menu-item--indent",
	)
}

function Entry({ item, columns }: { item: MenuItem; columns: Columns }) {
	if (item.submenu !== undefined)
		return <Submenu item={item} submenu={item.submenu} columns={columns} />
	if (item.checked !== undefined) {
		return (
			<RadixMenu.CheckboxItem
				className={rowClass(item)}
				checked={item.checked}
				disabled={item.disabled ?? false}
				onCheckedChange={item.onSelect}
				aria-keyshortcuts={item.shortcutAria ?? item.shortcut}
			>
				<Row item={item} columns={columns} />
			</RadixMenu.CheckboxItem>
		)
	}
	return (
		<RadixMenu.Item
			className={rowClass(item)}
			disabled={item.disabled ?? false}
			onSelect={item.onSelect}
			aria-keyshortcuts={item.shortcutAria ?? item.shortcut}
			aria-label={item.spoken}
		>
			<Row item={item} columns={columns} />
		</RadixMenu.Item>
	)
}

/*
 * An entry that opens its own menu beside the panel. The inner panel is portalled to the body like the
 * outer one; it mounts after the outer panel made the rest of the page inert (`inertOutside` reads the
 * body's children once, at the open), so it is never one of the children it marked. Its rows are
 * drawn by the same entry as the outer panel's, with their own columns.
 */
function Submenu({
	item,
	submenu,
	columns,
}: {
	item: MenuItem
	submenu: NonNullable<MenuItem["submenu"]>
	columns: Columns
}) {
	const inner: Columns = {
		icons: submenu.items.some((entry) => entry.icon !== undefined),
		marks: submenu.items.some((entry) => entry.checked !== undefined),
	}
	return (
		<RadixMenu.Sub>
			<RadixMenu.SubTrigger className={rowClass(item)} disabled={item.disabled ?? false}>
				<Row item={item} columns={columns} />
				<span className="tb-menu-item__more" aria-hidden="true">
					<Icon name="chevronRight" size={16} />
				</span>
			</RadixMenu.SubTrigger>
			<RadixMenu.Portal>
				<RadixMenu.SubContent className="tb-elevated tb-surface tb-menu" sideOffset={4}>
					{submenu.heading === undefined ? null : (
						<RadixMenu.Label className="tb-menu-subheading">{submenu.heading}</RadixMenu.Label>
					)}
					{submenu.items.map((entry) => (
						<Fragment key={entry.id}>
							{entry.separatorBefore === true ? (
								<RadixMenu.Separator className="tb-menu-separator" />
							) : null}
							<Entry item={entry} columns={inner} />
						</Fragment>
					))}
				</RadixMenu.SubContent>
			</RadixMenu.Portal>
		</RadixMenu.Sub>
	)
}

/*
 * One choice as a radio group, under its heading. The heading is drawn and not only announced: three
 * options between *Mute* and *Copy link* with nothing above them say what they do and not what is
 * being chosen, and a reader who can see them was the one reader not told (X-B1 P2). It is the same
 * small uppercase line the quick switcher stands over "CHANNELS", and it names the group for
 * assistive technology too, so the `aria-label` it replaces is not missed.
 */
function Options({ items, columns }: { items: MenuItem[]; columns: Columns }) {
	const held = items.find((item) => item.selected === true)
	const heading = items.find((item) => item.groupLabel !== undefined)?.groupLabel
	const headingId = useId()
	return (
		<RadixMenu.RadioGroup
			{...(heading === undefined ? {} : { "aria-labelledby": headingId })}
			value={held?.id ?? ""}
			onValueChange={(id) => items.find((item) => item.id === id)?.onSelect()}
		>
			{heading === undefined ? null : (
				<RadixMenu.Label className="tb-menu-heading" id={headingId}>
					{heading}
				</RadixMenu.Label>
			)}
			{items.map((item) => (
				<RadixMenu.RadioItem
					key={item.id}
					className={rowClass(item)}
					value={item.id}
					disabled={item.disabled ?? false}
					aria-keyshortcuts={item.shortcutAria ?? item.shortcut}
					aria-label={item.spoken}
				>
					<Row item={item} columns={columns} />
				</RadixMenu.RadioItem>
			))}
		</RadixMenu.RadioGroup>
	)
}

export function Menu({
	trigger,
	header,
	items,
	open,
	onOpenChange,
	onCloseAutoFocus,
	align = "end",
}: MenuProps) {
	const columns: Columns = {
		icons: items.some((item) => item.icon !== undefined),
		marks: items.some((item) => item.checked !== undefined || item.radioGroup !== undefined),
	}
	const triggerRef = useRef<HTMLButtonElement | null>(null)
	const contentRef = useRef<HTMLDivElement | null>(null)
	const [ownOpen, setOwnOpen] = useState(false)
	// The panel as it is mounted: the primitive mounts it a render after the open, in its portal.
	const [panel, setPanel] = useState<HTMLDivElement | null>(null)
	const shown = open ?? ownOpen
	useEffect(() => (shown ? inertOutside(panel) : undefined), [shown, panel])
	// The caller's handler as of the *latest* commit, not as of the render the menu was opened in: the
	// entry that was chosen usually sets some state first, and the hand-over below asks its question a
	// frame later, when that state is what the caller reads.
	const handOver = useRef(onCloseAutoFocus)
	useEffect(() => {
		handOver.current = onCloseAutoFocus
	}, [onCloseAutoFocus])

	/**
	 * The focus, given back one frame after the close. A frame is enough for the chosen entry's own
	 * work — a dialog that opens, an editor that mounts, a picker the caller asks for — and short
	 * enough that no key can be pressed inside it; whoever has the focus by then keeps it, and the
	 * trigger takes it back only when the choice left it in the closing panel or nowhere.
	 */
	function returnFocus(): void {
		requestAnimationFrame(() => {
			const asked = new Event("closeautofocus", { cancelable: true })
			handOver.current?.(asked)
			if (asked.defaultPrevented) return
			const active = document.activeElement
			const stranded =
				active === null || active === document.body || contentRef.current?.contains(active) === true
			if (!stranded) return
			triggerRef.current?.focus()
		})
	}

	return (
		<RadixMenu.Root
			modal={false}
			{...(open === undefined ? {} : { open })}
			onOpenChange={(next) => {
				setOwnOpen(next)
				// The panel that is leaving stays in the document for the length of its exit fade, and the
				// primitive's focus trap is armed for all of it: a surface the chosen entry opens and that
				// focuses itself a frame later would have the focus pulled back into a panel that is about
				// to vanish — and dropped on the body when it does, taking the keystrokes typed straight
				// after with it and telling a popover that the focus has left it. A container nothing can
				// focus ends that: the fade runs, and whatever the reader's own choice opened keeps what it
				// was given. The attribute is lifted again when the same panel is shown a second time
				// inside its own fade, which is the one case in which the node is reused (`inertOutside`,
				// which runs for an opening by the owner's `open` as well as for one through the trigger).
				if (!next) contentRef.current?.setAttribute("inert", "")
				onOpenChange?.(next)
				if (!next) returnFocus()
			}}
		>
			<RadixMenu.Trigger asChild ref={triggerRef}>
				{trigger}
			</RadixMenu.Trigger>
			<RadixMenu.Portal>
				<RadixMenu.Content
					ref={(node) => {
						contentRef.current = node
						setPanel(node)
					}}
					className="tb-elevated tb-surface tb-menu"
					// A long menu scrolls its rows (`.tb-menu`), and a scrolling region is reached from the
					// keyboard only if it is focusable itself while no row is: the panel is a stop of its
					// own. Tab never leaves an open menu (the primitive keeps it), so this adds no stop to
					// the page.
					tabIndex={0}
					sideOffset={4}
					align={align}
					// Declined, always: the primitive's hand-over happens at unmount, which is after the
					// exit fade. `returnFocus` above has done it a tenth of a second earlier.
					onCloseAutoFocus={(event) => event.preventDefault()}
					// A press on the trigger while the panel is leaving is the door being used again, not a
					// press beside it: the panel is still in the document, so it is still listening for a
					// press outside itself, and without this it dismisses the menu that the same press has
					// just opened — the door answers with a flicker for as long as the fade lasts.
					onPointerDownOutside={(event) => {
						if (triggerRef.current?.contains(event.target as Node) === true) event.preventDefault()
					}}
				>
					{header}
					{blocksOf(items).map((block) => (
						<Fragment key={block.items[0]?.id}>
							{block.items[0]?.separatorBefore === true ? (
								<RadixMenu.Separator className="tb-menu-separator" />
							) : null}
							{block.radioGroup === undefined ? (
								block.items.map((item) => <Entry key={item.id} item={item} columns={columns} />)
							) : (
								<Options items={block.items} columns={columns} />
							)}
						</Fragment>
					))}
				</RadixMenu.Content>
			</RadixMenu.Portal>
		</RadixMenu.Root>
	)
}

/**
 * Makes every child of the body inert but the one that holds `panel` — and the panel itself a menu
 * again, should it be the leaving one shown anew — and answers how to undo it: only the attributes this
 * call set are lifted, so a page that made something inert itself keeps it.
 */
function inertOutside(panel: HTMLElement | null): (() => void) | undefined {
	if (panel === null) return undefined
	panel.removeAttribute("inert")
	const set = [...document.body.children].filter(
		(child) => !child.contains(panel) && !child.hasAttribute("inert"),
	)
	for (const child of set) child.setAttribute("inert", "")
	return () => {
		for (const child of set) child.removeAttribute("inert")
	}
}
